// Incrementally maintained per-route aggregation of shipment snapshots, the
// source of the route-cache and carrier-cache files in S3 that the id
// dispensers read for drivers and carriers.
//
// Tables (avocado/data/sql/0511_add_route_cache_tables.sql):
//   route_cache          one row per route with the id arrays the S3 file holds,
//                        published_at = NULL while the file is due for a write,
//                        removed_at set once the route left the window
//   route_cache_carrier  one row per carrier whose file is due for a write
//   route_cache_state    how far data_shipment.synced_at has been read, and
//                        when the last full pass ran
//
// An incremental refresh re-aggregates the routes touched since the previous
// refresh: by shipments synced since then (the sync pipeline deletes and
// reinserts a shipment with its snapshots, comments and change docs on every
// sync, and stamps synced_at on insert, and records a shipment it removed in
// shipment_deleted), by OTP docs created since then, and by routes updated
// since then. A full refresh (`full: true`, scheduled once a day by its own
// Lambda) re-aggregates every route in the window to pick up what has no
// sync timestamp: master data changes and routes ageing out of the window.
// The first refresh of an empty table is always a full one.
//
// Both kinds lock the single state row for the duration of the refresh. An
// incremental refresh that finds the row locked (a full one is running) gives
// up at once and reports `skipped: true`; the next tick tries again.
//
// Rows whose content did not change are left alone, so the producer writes
// only the files that differ. Every unpublished record carries a `version`
// (changed_at as text); markPublished only stamps a row whose version still
// matches, so a file written from a listing that a concurrent refresh has
// overtaken is not mistaken for current.

const DEFAULT_STATEMENT_TIMEOUT_MS = 110000

// The full refresh sorts and hashes the whole snapshot window in memory;
// with the server default of 16 MB it spills to temp files.
const WORK_MEM = '128MB'

// SQLSTATE of "could not obtain lock on row" (FOR UPDATE NOWAIT)
const LOCK_NOT_AVAILABLE = '55P03'

// synced_at is the start time of the sync transaction, which may commit after
// a refresh read. The next refresh reads this much further back to catch it:
// the sync transform Lambda runs up to 300 s, plus a margin. Routes whose
// aggregation did not change cost one comparison and no write.
const SYNC_OVERLAP = '6 minutes'

const FILE_COLUMNS = [
  'couchdb_ids', 'comment_doc_ids', 'change_doc_ids', 'otp_doc_ids',
  'location_ids', 'funder_ids', 'service_ids', 'product_ids', 'batch_ids'
]
const CONTENT_COLUMNS = ['carrier_id', 'last_updated', 'shipment_ids', ...FILE_COLUMNS]
// last_updated moves whenever a snapshot document is edited; the id lists do
// not, and the dispensers read only ids, so it does not make a file stale.
const COMPARED_COLUMNS = CONTENT_COLUMNS.filter(column => column !== 'last_updated')

const sortedDistinct = (expr) => `ARRAY_AGG(DISTINCT ${expr} ORDER BY ${expr}) FILTER (WHERE ${expr} IS NOT NULL)`

// One row per route, every array sorted and deduplicated so that two
// aggregations of the same data compare equal.
const aggregationQuery = ({ withRouteFilter }) => `
  WITH snapshots AS MATERIALIZED (
    SELECT ss.id, ss.route_id, ss.couchdb_id, ss.shipment_id,
           ss.origin_id, ss.destination_id, ss.funder_id, ss.program_id,
           ss.updated_at, r.carrier_id::text AS carrier_id
    FROM avocado.data_shipmentsnapshot ss
    LEFT JOIN avocado.data_route r ON ss.route_id = r.couchdb_id
    WHERE ss.route_id IS NOT NULL
      AND ss.couchdb_id IS NOT NULL
      ${withRouteFilter ? 'AND ss.route_id = ANY($2::text[])' : ''}
      -- a snapshot stays in the window while it or its route was touched
      -- recently: assigning a carrier bumps the route, not the snapshots
      AND (ss.updated_at >= $1 OR r.updated_at >= $1)
  ),
  routes AS (
    SELECT route_id,
           MAX(carrier_id) AS carrier_id,
           MAX(updated_at) AS last_updated,
           ${sortedDistinct('shipment_id')} AS shipment_ids,
           ${sortedDistinct('couchdb_id')} AS couchdb_ids,
           ${sortedDistinct('funder_id')} AS funder_ids
    FROM snapshots
    GROUP BY route_id
  ),
  locations AS (
    SELECT route_id, ${sortedDistinct('location_id')} AS location_ids
    FROM (
      SELECT route_id, origin_id AS location_id FROM snapshots
      UNION ALL
      SELECT route_id, destination_id FROM snapshots
    ) l
    GROUP BY route_id
  ),
  services AS (
    SELECT rp.route_id, ${sortedDistinct('sv.fsid')} AS service_ids
    FROM (SELECT DISTINCT route_id, program_id FROM snapshots WHERE program_id IS NOT NULL) rp
    JOIN avocado.data_program p ON rp.program_id = p.fsid
    JOIN avocado.data_service sv ON p.id = sv.program_id
    GROUP BY rp.route_id
  ),
  line_facts AS (
    SELECT DISTINCT s.route_id, 'product:' || ssl.sku AS product_id, ssld.batch
    FROM snapshots s
    JOIN avocado.data_shipmentsnapshotline ssl ON ssl.shipment_snapshot_id = s.id
    LEFT JOIN avocado.data_shipmentsnapshotline_details ssld ON ssld.shipmentsnapshot_line = ssl.id
  ),
  lines AS (
    SELECT route_id,
           ${sortedDistinct('product_id')} AS product_ids,
           ${sortedDistinct('batch')} AS batch_ids
    FROM line_facts
    GROUP BY route_id
  ),
  changes AS (
    SELECT s.route_id, ${sortedDistinct('sc.couchdb_id')} AS change_doc_ids
    FROM snapshots s
    JOIN avocado.data_shipmentsnapshot_change sc ON sc.snapshot_couchdb_id = s.couchdb_id
    GROUP BY s.route_id
  ),
  shipments AS (
    SELECT DISTINCT route_id, shipment_id FROM snapshots WHERE shipment_id IS NOT NULL
  ),
  otps AS (
    SELECT sh.route_id, ${sortedDistinct('so.couchdb_id')} AS otp_doc_ids
    FROM shipments sh
    JOIN avocado.shipment_otp so ON so.shipment_id = sh.shipment_id
    GROUP BY sh.route_id
  ),
  comments AS (
    SELECT sh.route_id, ${sortedDistinct('sc.couchdb_id')} AS comment_doc_ids
    FROM shipments sh
    JOIN avocado.data_shipment_comment sc ON sc.shipment_id = sh.shipment_id
    GROUP BY sh.route_id
  )
  SELECT r.route_id,
         r.carrier_id,
         r.last_updated,
         COALESCE(r.shipment_ids, '{}') AS shipment_ids,
         COALESCE(r.couchdb_ids, '{}') AS couchdb_ids,
         COALESCE(cm.comment_doc_ids, '{}') AS comment_doc_ids,
         COALESCE(ch.change_doc_ids, '{}') AS change_doc_ids,
         COALESCE(ot.otp_doc_ids, '{}') AS otp_doc_ids,
         COALESCE(l.location_ids, '{}') AS location_ids,
         COALESCE(r.funder_ids, '{}') AS funder_ids,
         COALESCE(sv.service_ids, '{}') AS service_ids,
         COALESCE(ln.product_ids, '{}') AS product_ids,
         COALESCE(ln.batch_ids, '{}') AS batch_ids
  FROM routes r
  LEFT JOIN locations l USING (route_id)
  LEFT JOIN services sv USING (route_id)
  LEFT JOIN lines ln USING (route_id)
  LEFT JOIN changes ch USING (route_id)
  LEFT JOIN otps ot USING (route_id)
  LEFT JOIN comments cm USING (route_id)`

// Routes touched since `since`: through their shipments (current and previous
// route of each synced or deleted shipment), through new OTP docs, and directly.
const affectedRoutesQuery = `
  WITH changed AS (
    SELECT shipment_id FROM avocado.data_shipment WHERE synced_at >= $1
    UNION
    SELECT shipment_id FROM avocado.shipment_deleted WHERE deleted_at >= $1
    UNION
    SELECT shipment_id FROM avocado.shipment_otp WHERE created_at >= $1
  )
  SELECT DISTINCT route_id FROM (
    SELECT ss.route_id
    FROM changed c
    JOIN avocado.data_shipmentsnapshot ss ON ss.shipment_id = c.shipment_id
    WHERE ss.route_id IS NOT NULL
    UNION
    SELECT rc.route_id
    FROM avocado.route_cache rc
    WHERE rc.shipment_ids && (SELECT COALESCE(ARRAY_AGG(shipment_id), '{}') FROM changed)
    UNION
    SELECT couchdb_id FROM avocado.data_route WHERE updated_at >= $1
  ) affected`

// Aggregates, rewrites the rows whose content differs and tombstones the
// routes without snapshots in the window any more, in one statement. The
// aggregation is referenced twice and therefore computed once.
const applyQuery = ({ withRouteFilter }) => `
  WITH agg AS (${aggregationQuery({ withRouteFilter })}),
  upserted AS (
    INSERT INTO avocado.route_cache (route_id, ${CONTENT_COLUMNS.join(', ')})
    SELECT route_id, ${CONTENT_COLUMNS.join(', ')} FROM agg
    ON CONFLICT (route_id) DO UPDATE SET
      ${CONTENT_COLUMNS.map(column => `${column} = EXCLUDED.${column}`).join(',\n      ')},
      changed_at = now(),
      published_at = NULL,
      removed_at = NULL
    WHERE route_cache.removed_at IS NOT NULL
       OR (${COMPARED_COLUMNS.map(column => `route_cache.${column}`).join(', ')})
          IS DISTINCT FROM
          (${COMPARED_COLUMNS.map(column => `EXCLUDED.${column}`).join(', ')})
    RETURNING route_id, carrier_id
  ),
  removed AS (
    UPDATE avocado.route_cache rc
    SET removed_at = now(), changed_at = now(), published_at = NULL
    WHERE rc.removed_at IS NULL
      ${withRouteFilter ? 'AND rc.route_id = ANY($2::text[])' : ''}
      AND NOT EXISTS (SELECT 1 FROM agg WHERE agg.route_id = rc.route_id)
    RETURNING route_id, carrier_id
  )
  SELECT route_id, carrier_id, false AS removed FROM upserted
  UNION ALL
  SELECT route_id, carrier_id, true AS removed FROM removed`

const mergedOverCarrier = (column) => `
    (SELECT COALESCE(ARRAY_AGG(DISTINCT x ORDER BY x), '{}')
     FROM avocado.route_cache r2, UNNEST(r2.${column}) x
     WHERE r2.carrier_id = c.carrier_id AND r2.removed_at IS NULL) AS ${column}`

const unpublishedCarriersQuery = `
  SELECT c.carrier_id, c.changed_at::text AS version,
         COALESCE(ARRAY_AGG(r.route_id ORDER BY r.route_id) FILTER (WHERE r.route_id IS NOT NULL), '{}') AS route_ids,
         MAX(r.last_updated) AS last_updated,
         ${['location_ids', 'funder_ids', 'service_ids', 'product_ids', 'batch_ids'].map(mergedOverCarrier).join(',')}
  FROM avocado.route_cache_carrier c
  LEFT JOIN avocado.route_cache r ON r.carrier_id = c.carrier_id AND r.removed_at IS NULL
  WHERE c.published_at IS NULL
  GROUP BY c.carrier_id, c.changed_at
  ORDER BY c.carrier_id`

const camel = (column) => column.replace(/_([a-z])/g, (_, c) => c.toUpperCase())

const toRouteRecord = (row) => {
  const record = { routeId: row.route_id, carrierId: row.carrier_id, lastUpdated: row.last_updated, removed: row.removed_at != null, version: row.version }
  for (const column of FILE_COLUMNS) record[camel(column)] = row[column]
  return record
}

const toCarrierRecord = (row) => ({
  carrierId: row.carrier_id,
  version: row.version,
  routeIds: row.route_ids,
  lastUpdated: row.last_updated,
  locationIds: row.location_ids,
  funderIds: row.funder_ids,
  serviceIds: row.service_ids,
  productIds: row.product_ids,
  batchIds: row.batch_ids,
  // a carrier without live routes has no file any more
  removed: row.route_ids.length === 0
})

class RouteCacheAdapter {
  constructor (pgConnection, logger) {
    this.pgConnection = pgConnection
    this.logger = logger
  }

  /**
   * Brings avocado.route_cache up to date with the shipment tables.
   *
   * @param {object} options
   * @param {string} options.cutoffDate ISO date, snapshots (or their routes) updated before it are out of the window
   * @param {boolean} [options.full] re-aggregate every route in the window instead of the routes touched since the previous refresh
   * @param {number} [options.statementTimeoutMs] PG statement_timeout for the refresh transaction
   * @returns {Promise<{mode: 'full'|'incremental', skipped: boolean, affectedRoutes: number|null, changedRoutes: number, removedRoutes: number, carriers: number}>}
   */
  async refresh ({ cutoffDate, full = false, statementTimeoutMs = DEFAULT_STATEMENT_TIMEOUT_MS }) {
    if (!cutoffDate) throw new Error('cutoffDate is required')

    try {
      return await this.transaction(statementTimeoutMs, (client) => this.refreshLocked(client, { cutoffDate, full }))
    } catch (error) {
      if (error.code === LOCK_NOT_AVAILABLE) {
        if (this.logger) this.logger.info({ code: 'route-cache-refresh-skipped', reason: 'another refresh holds the state lock' })
        return { mode: 'incremental', skipped: true, affectedRoutes: 0, changedRoutes: 0, removedRoutes: 0, carriers: 0 }
      }
      if (this.logger) this.logger.error({ code: 'route-cache-refresh-failed', error: error.message })
      throw error
    }
  }

  async refreshLocked (client, { cutoffDate, full: fullRequested }) {
    // One state row, locked for the duration of the refresh. The full
    // refresh waits for a running incremental one (sub-second); an
    // incremental refresh does not wait for a running full one.
    await client.query(`INSERT INTO avocado.route_cache_state (id, synced_until) VALUES (1, '-infinity') ON CONFLICT (id) DO NOTHING`)
    const { rows: [state] } = await client.query(`
      SELECT synced_until - $1::interval AS since, full_refresh_at IS NULL AS never_built
      FROM avocado.route_cache_state WHERE id = 1 FOR UPDATE${fullRequested ? '' : ' NOWAIT'}`,
    [SYNC_OVERLAP])
    const full = fullRequested || state.never_built

    let affected = null
    if (!full) {
      const { rows } = await client.query(affectedRoutesQuery, [state.since])
      affected = rows.map(row => row.route_id)
    }

    const previous = await client.query(
      `SELECT route_id, carrier_id FROM avocado.route_cache${full ? '' : ' WHERE route_id = ANY($1::text[])'}`,
      full ? [] : [affected]
    )
    const previousCarrier = new Map(previous.rows.map(row => [row.route_id, row.carrier_id]))

    const { rows: applied } = await client.query(applyQuery({ withRouteFilter: !full }), full ? [cutoffDate] : [cutoffDate, affected])

    // Both the carrier a route is on now and the one it was on before get a
    // new file.
    const carriers = new Set()
    for (const row of applied) {
      if (row.carrier_id) carriers.add(row.carrier_id)
      const before = previousCarrier.get(row.route_id)
      if (before) carriers.add(before)
    }
    if (carriers.size) {
      await client.query(`
        INSERT INTO avocado.route_cache_carrier (carrier_id)
        SELECT UNNEST($1::text[])
        ON CONFLICT (carrier_id) DO UPDATE SET published_at = NULL, changed_at = now()`,
      [Array.from(carriers)])
    }

    if (full) await client.query('DELETE FROM avocado.shipment_deleted WHERE deleted_at < $1', [cutoffDate])
    await client.query(`
      UPDATE avocado.route_cache_state
      SET synced_until = now(), full_refresh_at = CASE WHEN $1 THEN now() ELSE full_refresh_at END
      WHERE id = 1`,
    [full])

    return {
      mode: full ? 'full' : 'incremental',
      skipped: false,
      affectedRoutes: full ? null : affected.length,
      changedRoutes: applied.filter(row => !row.removed).length,
      removedRoutes: applied.filter(row => row.removed).length,
      carriers: carriers.size
    }
  }

  /**
   * The routes and carriers whose S3 file differs from the table.
   * A record with `removed: true` has no file any more.
   */
  async listUnpublished () {
    const routes = await this.pgConnection.query(`
      SELECT route_id, carrier_id, last_updated, removed_at, changed_at::text AS version, ${FILE_COLUMNS.join(', ')}
      FROM avocado.route_cache
      WHERE published_at IS NULL
      ORDER BY route_id`)
    const carriers = await this.pgConnection.query(unpublishedCarriersQuery)
    return {
      routes: routes.rows.map(toRouteRecord),
      carriers: carriers.rows.map(toCarrierRecord)
    }
  }

  /**
   * Records that the S3 files of these routes and carriers match the table.
   * Takes the records from listUnpublished; a row whose version moved since
   * the listing is left unpublished. Removed routes and carriers without live
   * routes are deleted.
   */
  async markPublished ({ routes = [], carriers = [] }) {
    if (routes.length) {
      const params = [routes.map(r => r.routeId), routes.map(r => r.version)]
      await this.pgConnection.query(`
        DELETE FROM avocado.route_cache rc
        USING UNNEST($1::text[], $2::timestamptz[]) AS p (route_id, version)
        WHERE rc.route_id = p.route_id AND rc.changed_at = p.version AND rc.removed_at IS NOT NULL`, params)
      await this.pgConnection.query(`
        UPDATE avocado.route_cache rc
        SET published_at = now()
        FROM UNNEST($1::text[], $2::timestamptz[]) AS p (route_id, version)
        WHERE rc.route_id = p.route_id AND rc.changed_at = p.version`, params)
    }
    if (carriers.length) {
      const params = [carriers.map(c => c.carrierId), carriers.map(c => c.version)]
      await this.pgConnection.query(`
        DELETE FROM avocado.route_cache_carrier c
        USING UNNEST($1::text[], $2::timestamptz[]) AS p (carrier_id, version)
        WHERE c.carrier_id = p.carrier_id AND c.changed_at = p.version
          AND NOT EXISTS (SELECT 1 FROM avocado.route_cache r WHERE r.carrier_id = c.carrier_id AND r.removed_at IS NULL)`, params)
      await this.pgConnection.query(`
        UPDATE avocado.route_cache_carrier c
        SET published_at = now()
        FROM UNNEST($1::text[], $2::timestamptz[]) AS p (carrier_id, version)
        WHERE c.carrier_id = p.carrier_id AND c.changed_at = p.version`, params)
    }
  }

  // Runs `fn` on one pinned client inside a transaction. The statement timeout
  // kills a slow refresh before the Lambda does, so Postgres never keeps
  // executing a statement whose client is gone (issue 25067).
  async transaction (statementTimeoutMs, fn) {
    // pg Pool or an already-connected Client (test harness)
    const isPool = typeof this.pgConnection.idleCount === 'number'
    let client
    try {
      client = isPool ? await this.pgConnection.connect() : this.pgConnection
      await client.query('BEGIN')
      await client.query(`SET LOCAL statement_timeout = ${Number(statementTimeoutMs)}`)
      await client.query(`SET LOCAL work_mem = '${WORK_MEM}'`)
      const result = await fn(client)
      await client.query('COMMIT')
      return result
    } catch (error) {
      if (client) await client.query('ROLLBACK').catch(() => {})
      throw error
    } finally {
      if (isPool && client) client.release()
    }
  }
}

module.exports = RouteCacheAdapter
